import { FileText, Info, HelpCircle, Phone, Mail, Route, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { PHONE, EMAIL } from "@/components/Layout";

const DOCS = [
  {
    title: "Passenger Guide",
    Icon: Route,
    accent: "bg-blue-50 text-[#1D4ED8]",
    paragraphs: [
      "Our drivers are trained to provide door-to-door, non-emergency medical transportation. On the day of your ride, please be ready 15 minutes before your scheduled pickup time."
    ],
  },
  {
    title: "Privacy Notice",
    Icon: Lock,
    accent: "bg-slate-100 text-slate-700",
    paragraphs: [
      "Moreno Quality Transportation collects only the information needed to arrange and deliver your transportation: your name, contact details, pickup and drop-off addresses, appointment time, mobility needs, and, when applicable, insurance or transportation-broker identifiers. We do not sell your personal information, and we do not use it for advertising."
    ],
  },
];

export default function Patients() {
  return (
    <div className="animate-fade-up">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-3">For Patients</div>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#0A192F] max-w-3xl">Helpful information for our passengers</h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl leading-relaxed">Everything you may want to know about your ride with Moreno Quality Transportation. No account required.</p>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#1D4ED8] flex items-center justify-center"><Info className="w-5 h-5" /></div>
            <h3 className="mt-4 text-lg font-semibold text-[#0A192F]">Transportation info</h3>
            <p className="mt-2 text-sm text-slate-600">Learn what to expect before, during, and after your ride with our professional drivers.</p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#1D4ED8] flex items-center justify-center"><HelpCircle className="w-5 h-5" /></div>
            <h3 className="mt-4 text-lg font-semibold text-[#0A192F]">Common questions</h3>
            <p className="mt-2 text-sm text-slate-600">Answers on scheduling, wheelchair accessibility, senior support, and hospital transport.</p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Phone className="w-5 h-5" /></div>
            <h3 className="mt-4 text-lg font-semibold text-[#0A192F]">Talk to us</h3>
            <p className="mt-2 text-sm text-slate-600">Have questions? Contact our team by phone or email — we're happy to help.</p>
            <div className="mt-3 flex flex-col gap-1.5 text-sm">
              <a href={`tel:${PHONE.replace(/\D/g, "")}`} className="inline-flex items-center gap-2 text-[#1D4ED8] font-semibold"><Phone className="w-4 h-4" /> {PHONE}</a>
              <a href={`mailto:${EMAIL}`} className="inline-flex items-center gap-2 text-[#1D4ED8] font-semibold break-all"><Mail className="w-4 h-4" /> {EMAIL}</a>
            </div>
          </div>
        </div>

        <h2 id="documents" className="mt-16 text-2xl sm:text-3xl font-bold text-[#0A192F]">Passenger information</h2>
        <p className="mt-2 text-sm text-slate-500">Everything you need to know about riding with us — no downloads required.</p>
        <div className="mt-6 space-y-5">
          {DOCS.map((d) => (
            <article key={d.title} className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-7">
              <header className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl ${d.accent} flex items-center justify-center shrink-0`}><d.Icon className="w-5 h-5" /></div>
                <h3 className="text-xl font-semibold text-[#0A192F] leading-snug pt-1">{d.title}</h3>
              </header>
              <div className="mt-4 space-y-3 text-slate-700 leading-relaxed text-[15px]">
                {d.paragraphs.map((p, i) => (<p key={i}>{p}</p>))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 rounded-3xl bg-gradient-to-br from-blue-50 to-emerald-50 border border-slate-200 p-10 text-center">
          <h3 className="text-2xl font-bold text-[#0A192F]">Need to schedule or ask something?</h3>
          <p className="mt-2 text-slate-600">Reach out to our team — no online account required.</p>
          <Link to="/contact" className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1D4ED8] text-white font-semibold hover:bg-blue-800 transition-colors">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}
